// src/nexus/proofPulse.js
//
// Proof-world extractor for the "Portfolio Outcome Proof" evidence row in
// HqsCommandCenter's ProofView. Mirrors the validate-or-null discipline
// established in systemPulse.js/operationsPulse.js/learningPulse.js.
//
// Root cause this documents (does NOT fix — this is a backend defect, out
// of scope for this frontend-only change): /api/admin/performance-proof
// (services/performanceProof.service.js#buildPerformanceProofSummary,
// davidhuecker-bit/hqs-backend) computes proofScore as a 0-100 composite,
// then re-multiplies it by 100 a second time before clamping to
// [0, 100] — `proofScore = Math.min(100, Math.max(0, proofScore * 100))`
// where `proofScore` on the right-hand side is ALREADY on a 0-100 scale.
// The practical effect: any non-empty decision set (verifiedOutcomes = 0
// included, since a missing winRate defaults to a neutral 0.5 factor)
// produces a raw composite of roughly 15-100, which the second `* 100`
// blows past the clamp ceiling — so proofScore reads 100 almost
// unconditionally, even with zero verified outcomes. This is exactly the
// reported incident: 50 decisions, 0 verified outcomes, 50 pending ->
// proofScore: 100.
//
// This extractor cannot repair the backend's arithmetic from the frontend
// (that would mean silently inventing a "corrected" score, which is exactly
// the kind of fabrication this whole normalizer layer exists to prevent).
// Instead it fails closed at the DISPLAY layer: `hasVerifiedEvidence` is
// only true when verifiedOutcomes is a real, positive number, and callers
// (ProofView) must never present the raw proofScore as trustworthy "live"
// proof when it is false — see ProofView's evidence-row wiring.
//
// Verified against the real backend response shape by reading
// services/performanceProof.service.js#buildPerformanceProofSummary and its
// route handler directly (davidhuecker-bit/hqs-backend):
//   GET /api/admin/performance-proof?window=30d&limit=50 ->
//     { success, data: { totalDecisions, openedPositions, heldPositions,
//       closedPositions, verifiedOutcomes, pendingOutcomes,
//       insufficientDataCount, winRate, currentOpenPnL, realizedPnL,
//       proofScore } }

const USABLE_ENDPOINT_STATES = new Set(["live", "partial"]);

function findEndpoint(endpointResults, id) {
  if (!Array.isArray(endpointResults)) return null;
  return endpointResults.find((entry) => entry && entry.id === id) || null;
}

function isUsable(endpoint) {
  return !!endpoint && USABLE_ENDPOINT_STATES.has(endpoint.state);
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function isNonNegativeInteger(value) {
  return isFiniteNumber(value) && Number.isInteger(value) && value >= 0;
}

function endpointBody(endpoint) {
  if (!endpoint) return null;
  const raw = endpoint.data;
  return isPlainObject(raw) ? raw : null;
}

function pushMissing(missingFields, field) {
  if (!missingFields.includes(field)) missingFields.push(field);
}

export function extractPerformanceProof(endpointResults, missingFields = []) {
  const endpoint = findEndpoint(endpointResults, "performance-proof");
  if (!isUsable(endpoint)) {
    pushMissing(missingFields, "performanceProof");
    return null;
  }
  const body = endpointBody(endpoint);
  const data = isPlainObject(body?.data) ? body.data : null;
  if (!data) {
    pushMissing(missingFields, "performanceProof");
    return null;
  }
  const totalDecisions = isNonNegativeInteger(data.totalDecisions) ? data.totalDecisions : null;
  const verifiedOutcomes = isNonNegativeInteger(data.verifiedOutcomes) ? data.verifiedOutcomes : null;
  const pendingOutcomes = isNonNegativeInteger(data.pendingOutcomes) ? data.pendingOutcomes : null;
  const proofScore = isFiniteNumber(data.proofScore) ? data.proofScore : null;
  return {
    totalDecisions,
    verifiedOutcomes,
    pendingOutcomes,
    proofScore,
    winRate: isFiniteNumber(data.winRate) ? data.winRate : null,
    // Fail-closed: the raw proofScore may never be presented as live-proven
    // when there is no actual verified evidence backing it (see this file's
    // header comment for the exact backend arithmetic bug that makes this
    // happen almost unconditionally today).
    hasVerifiedEvidence: verifiedOutcomes !== null && verifiedOutcomes > 0,
  };
}

export function normalizeProofPulse({ endpointResults } = {}) {
  const missingFields = [];
  const performanceProof = extractPerformanceProof(endpointResults, missingFields);
  return { performanceProof, missingFields };
}
